/**
 * VideoWorkspacePanels - Studio Mode Design
 *
 * DESIGN: Linear/Framer inspired - Modern, Minimalist, Professional
 * - Supports Dark/Light themes
 * - Floating glass panels
 * - Dot grid canvas background
 * - Cinematic slide shadows
 * - Responsive edit toolbar placement
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useVideoWorkspaceStore, type Slide } from '@/stores/videoWorkspaceStore';
import { useConversationStore } from '@/stores/conversationStore';
import type { GsapSlidePreviewRef } from '@/components/slides/GsapSlidePreviewEnhanced';
import { VersionSwitcher } from '@/components/slides/VersionSwitcher';
import { EmbeddedIvyChat } from '@/components/agent-chat/EmbeddedIvyChat';
import { OutlineWorkspacePanel } from '@/components/outline/OutlineWorkspacePanel';
import { SmartAddSlideModal, SmartAddSlideData } from '@/components/outline/SmartAddSlideModal';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { unifiedMessageService } from '@/services/unifiedMessageService';
import { NarrationView } from './narration';
import { BlogStudioView } from './blog';
import { LabPanel } from './lab';
import { StoryboardPanel, UNSENT_CHANGES_PROMPT, useStoryboardExists } from './storyboard';
import { visibleWorkspaceTabs, resolveInitialTab, landingTab, LANDING_WAIT_MS, isFootageMode, keepsOwnScreens, foldedTab, type WorkspaceTab } from './workspaceTabs';
import { WorkspaceTabStrip, WorkspaceTabStripSkeleton } from './WorkspaceTabStrip';
import { WorkspaceViewBar } from './WorkspaceViewBar';
import { workspaceContext, storyDevelopmentContext } from '@/lib/ivyChat/systemContext';
import { StageBar } from '@/components/studio/StageBar';
import { StudioProgressB } from '@/components/studio/StudioProgressB';
import { StudioStateProvider } from '@/components/studio/StudioContext';
import { useStudioState, useNow } from '@/components/studio/studioHooks';
import { BuildView } from '@/components/studio/BuildView';
import { pickFocusScene, type BuildScene } from '@/lib/studio/buildScenes';
import type { VideoCompositionPlayerRef } from './VideoCompositionPlayer';
import { FlipText } from './FlipText';
import { StudioTopBar } from './StudioTopBar';
import { StudioTopBarB } from './StudioTopBarB';
import { StudioBarSlot } from './studioBarSlot';
import { studioBOn } from '@/lib/studioB/flag';
import { buildEta } from '@/lib/studioB/eta';
import { canResume } from '@/lib/studioB/waitAtScene';
import { stagePoster } from '@/lib/studioB/stagePoster';
import type { FrontDoorStart } from '@/lib/frontDoor/handoff';
import { useRoomForBothSides } from '@/lib/studioB/useRoomForBothSides';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useStudioFullscreen } from '@/lib/studioB/useStudioFullscreen';
import { SHEET_PEEK } from './StudioIvyColumn';
import { StudioIvyColumn } from './StudioIvyColumn';
import { useCentreStage } from '@/lib/studioB/useCentreStage';
import { StudioCanvas } from './StudioCanvas';
import { StudioEditToolbar } from './StudioEditToolbar';
import { StudioPlayerControls } from './StudioPlayerControls';
import { StudioFilmstrip } from './StudioFilmstrip';
import { StudioTimelineB } from './StudioTimelineB';
import { StudioAudioPanel } from './StudioAudioPanel';
import { StudioReviewsPanel } from './StudioReviewsPanel';
import { PhoneMoreSheetB } from './PhoneMoreSheetB';
import { slidesAPI, outlineAPI, videoManifestAPI, labAssetsAPI, getApiServerUrl } from '@/services/api';
import {
  buildVideoManifest,
  type VideoManifest,
  type VideoPlaybackState as CompositionPlaybackState,
  type WorkspaceSlide,
  type TransitionConfig,
} from '@/types/videoManifest';
import { toast } from 'sonner';
import { useTheme } from '@/hooks/useTheme';
import { useSceneNavigation } from '@/hooks/useSceneNavigation';
import type { WorkspaceMode } from './VideoWorkspaceShell';
import { whenWorkspaceCdnReady } from '@/lib/workspaceCdnReady';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useSceneSortSensors } from '@/lib/studio/sceneDrag';
import { AnimatePresence, motion } from 'framer-motion';
import { bFade, bMove } from '@/lib/studioB/motion';

// UI entry points only — everything behind these tabs stays wired.
// Distinct from _ADVANCED below, which gates whether narration is EDITABLE (beta: yes).
const SHOW_NARRATION_TAB = import.meta.env.VITE_SHOW_NARRATION_TAB === 'true';
const SHOW_BLOG_TAB = import.meta.env.VITE_SHOW_BLOG_TAB === 'true';
const SHOW_NARRATION_ADVANCED = import.meta.env.VITE_SHOW_NARRATION_ADVANCED === 'true';
/** Studio B: how long a scene that landed keeps its plan up while the player gets it, before Play is handed back. */
const RESUME_WAIT_MS = 8000;

export type { WorkspaceTab } from './workspaceTabs';

interface VideoWorkspacePanelsProps {
  conversationId: string;
  mode?: WorkspaceMode;
  initialTab?: WorkspaceTab;
  initialMessage?: string;
  creationMode?: string;
  /** What a front-door start handed over (the idea and the look), for Direction B's takeover. */
  frontDoor?: FrontDoorStart;
}

export const VideoWorkspacePanels: React.FC<VideoWorkspacePanelsProps> = ({
  conversationId,
  mode = 'video',
  initialTab,
  initialMessage,
  creationMode: loaderCreationMode,
  frontDoor,
}) => {
  const navigate = useNavigate();
  const previewRef = useRef<GsapSlidePreviewRef>(null);
  const compositionRef = useRef<VideoCompositionPlayerRef>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // Track pending return to composition mode after save/cancel
  // This prevents race conditions where we switch modes before save completes
  const pendingReturnToComposition = useRef<'save' | 'cancel' | null>(null);

  // Track if we entered edit mode from composition mode
  // This ensures we return to composition when edit mode exits (even after asset replacement)
  const enteredEditFromComposition = useRef<boolean>(false);

  // Track slide that needs invalidation after returning to composition mode
  // This is needed because compositionRef is null while in edit mode (VideoCompositionPlayer not mounted)
  const pendingSlideInvalidation = useRef<string | null>(null);

  // Track if we're waiting for save to complete before returning to composition
  // This prevents the race condition where we switch modes before save completes
  const waitingForSaveComplete = useRef<boolean>(false);

  // Suppress onSlideStart during return from edit to prevent resetting to first slide
  // When composition remounts after edit, it briefly reports first slide before seeking back
  const suppressSlideStartRef = useRef<boolean>(false);

  // Studio B: the scene playback waits at, and the one it is carrying on into. Only carrying on reloads that scene in
  // the player: the player says a scene reloaded even when a newer reload overtook it, so a second reload in flight
  // would let playback start on a half-loaded scene.
  const waitRef = useRef<{ id: string; path: string; loaded: boolean } | null>(null);
  const resumeRef = useRef<{ id: string; reloading: boolean; at: number | null; timer: number } | null>(null);
  const heldByB = (id: string) => waitRef.current?.id === id || resumeRef.current?.id === id;

  // Track outline slide order for detecting reorders and refreshing manifest
  const prevOutlineOrderRef = useRef<string>('');

  // Track if we're in the middle of a reorder operation (to skip auto-refresh)
  const isReorderingRef = useRef<boolean>(false);

  // Debounce timer for manifest refresh when outline changes externally
  const manifestRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mode-specific flags
  const isVideoMode = mode === 'video';
  // Note: isOutlineMode is no longer used - we use tab-based navigation instead

  // Get conversation store to clear active conversation on navigation
  const { setActiveConversation } = useConversationStore();

  const {
    slides,
    currentSlideId,
    setCurrentSlideId,
    followCanvasScene,
    currentSlideData,
    setCurrentSlideData,
    currentVersion,
    versions,
    setVersions,
    setCurrentVersion,
    playerState,
    setPlayerState,
    setEditToolbarState,
    previewRefreshKey,
    triggerPreviewRefresh,
    setPreviewSeekToEnd,
    isEditMode,
    setEditMode,
    editToolbarState,
    // Outline state
    outline,
    templateLayouts,
    reorderOutlineSlides,
    // Outline generation state
    isOutlineGenerating,
    outlineNeedsInput,
    // Slide generation tracking
    slideConversations,
    // Chat context
    activeChatContext,
    setActiveChatContext,
    // Edit isolation
    setEditingSlide,
    pendingSlideUpdate,
    clearPendingSlideUpdate,
    invalidateSlideCache,
  } = useVideoWorkspaceStore();

  // Get project resolution from narration context (defaults to 1920x1080)
  const projectResolution = useVideoWorkspaceStore((s) => s.getProjectResolution(conversationId));

  // Theme (persisted via useTheme hook)
  const { isDarkMode, toggleTheme } = useTheme();

  // (ivy-chat system context computed below, after activeTab is declared)

  // Panel visibility state
  // Chat panel visible by default when outline exists (studio/narration/outline views)
  // Hidden during story development (no outline yet, chat is in center canvas)
  // Direction B keeps Ivy open from the start: centred first, then the rail is already open the moment she moves.
  const [showChatPanel, setShowChatPanel] = useState(!!outline || studioBOn());
  const [showAudioPanel, setShowAudioPanel] = useState(false);
  const [showReviewsPanel, setShowReviewsPanel] = useState(false);
  const [showFilmstrip, setShowFilmstrip] = useState(true);

  // Composition mode state
  // When true: VideoCompositionPlayer shows full video playback with transitions
  // When false: GsapSlidePreviewEnhanced shows single slide with editing
  const [isCompositionMode, setCompositionMode] = useState(true);
  const [videoManifest, setVideoManifest] = useState<VideoManifest | null>(null);
  const narrationScripts = useVideoWorkspaceStore((s) => s.narrationCacheByConversation[conversationId]?.scripts);
  const studio = useStudioState({
    outline,
    hasManifest: !!videoManifest,
    manifestLoading: studioBOn() && isVideoMode && slides.length > 0 && !videoManifest,
    narration: narrationScripts,
  });
  const [compositionState, setCompositionState] = useState<CompositionPlaybackState>({
    isPlaying: false,
    currentTime: 0,
    totalDuration: 0,
    currentSlideId: '',
    currentSlideIndex: -1,
    currentSlideTime: 0,
    currentSlideDuration: 0,
    playbackRate: 1,
    isContinuousMode: true,
    isTransitioning: false,
  });
  const [_returnPosition, setReturnPosition] = useState(0); // Position to return to after editing (unused but kept for debug logging)

  // Tab state — outline / studio / narration / lab / blog
  // Blog tab unlocks once the video is fully assembled (all slides completed).
  const creationMode = outline?.creationMode ?? loaderCreationMode;
  const [activeTab, setActiveTabState] = useState<WorkspaceTab>(() => resolveInitialTab(initialTab, creationMode));
  const [storyboardPending, setStoryboardPending] = useState(0);
  const setActiveTab = useCallback((tab: WorkspaceTab) => {
    if (activeTab === 'storyboard' && tab !== 'storyboard' && storyboardPending > 0 && !window.confirm(UNSENT_CHANGES_PROMPT)) return;
    setActiveTabState(tab);
  }, [activeTab, storyboardPending]);
  const { exists: hasStoryboard, known: storyboardKnown } = useStoryboardExists(conversationId);

  // The Lab is a PRE-outline surface by design — the agent publishes prototypes there to
  // settle a direction before there is anything to outline — so it can't be gated on the
  // outline existing the way Studio/Narration are. Track whether the project has any lab
  // assets so the tab strip can appear during story development.
  const [hasLabAssets, setHasLabAssets] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { assets } = await labAssetsAPI.get(conversationId);
        if (!cancelled) setHasLabAssets(assets.length > 0);
      } catch (err) {
        console.error('[VideoWorkspacePanels] Failed to check lab assets', err);
      }
    };
    check();
    // Fired by conversationUpdatesService when sync_lab_asset_to_production publishes.
    const onUpdate = (e: Event) => {
      if ((e as CustomEvent).detail?.conversationId === conversationId) check();
    };
    window.addEventListener('lab-assets-updated', onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('lab-assets-updated', onUpdate);
    };
  }, [conversationId]);

  const ownScreens = keepsOwnScreens(outline as unknown as Parameters<typeof keepsOwnScreens>[0]);
  // Direction B (beta only): the video is made on the Studio stage, so there is no Scenes tab.
  const isB = studioBOn();
  // D9: on a phone, B's studio with Ivy full screen and then as a sheet, not today's mobile workspace.
  const isMobile = useIsMobile();
  const phone = isB && isMobile;
  const workspaceTabs = visibleWorkspaceTabs({
    creationMode,
    hasOutline: !!outline,
    hasLabAssets,
    hasStoryboard,
    showNarration: SHOW_NARRATION_TAB,
    showBlog: SHOW_BLOG_TAB,
    keepsOwnScreens: ownScreens,
    showOutline: initialTab === 'outline',
    videoReady: slides.length > 0 && slides.every((s) => s.status === 'completed'),
  });
  const visibleTabs = isB ? workspaceTabs.filter((t) => t !== 'scenes') : workspaceTabs;

  // The build view: each scene's state with its newest saved preview and the plan's sketch of it.
  const [focusSceneId, setFocusSceneId] = useState<string | undefined>();
  const buildScenes: BuildScene[] = useMemo(
    () => studio.scenes.map((scene) => ({
      ...scene,
      thumbnail: slides.find((x) => x.id === scene.id)?.thumbnail ?? scene.thumbnail,
      sketch: outline?.slides.find((x) => x.id === scene.id)?.description ?? '',
    })),
    [studio.scenes, slides, outline],
  );
  const buildNow = useNow(studio.working && (isB || activeTab === 'scenes'));

  // When the scenes start building, show them being built, once, unless you are somewhere else on purpose. Only a
  // build Ivy is running counts: a finished video's scenes arrive built before its video does, so its stage reads
  // "build" for a moment while it loads, and that must not take you off the tab you just picked.
  const reachedBuild = useRef(false);
  useEffect(() => {
    if (studio.stage !== 'build' || !studio.working || reachedBuild.current) return;
    reachedBuild.current = true;
    const buildTab = isB ? 'studio' : 'scenes';
    if (visibleTabs.includes(buildTab) && (activeTab === 'storyboard' || activeTab === 'outline')) setActiveTabState(buildTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studio.stage, studio.working]);

  // When the video is ready, Scenes leaves the strip and the video is in Studio.
  useEffect(() => {
    const next = foldedTab(activeTab, visibleTabs);
    if (next !== activeTab) setActiveTabState(next);
  }, [activeTab, visibleTabs]);

  // Once the living storyboard exists (V2-6), it is where review happens: land there unless a tab was asked for.
  useEffect(() => {
    if (isB) return;
    if (hasStoryboard && !ownScreens && !initialTab && activeTab === 'outline' && !isFootageMode(creationMode)) setActiveTabState('storyboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStoryboard, ownScreens]);

  // Direction B (beta only): Ivy stays centred until there is something to show, then moves to the rail (useCentreStage).
  // The project's outline exists from setup as a shell (a title, no scenes); it is the plan once it has a scene.
  const hasPlan = !!outline?.slides.some((s) => !s.isDeleted);
  // The shell renders before its outline fetch starts, so a reopened video's outline arrives after the first render.
  const outlineLoading = useVideoWorkspaceStore((st) => st.outlineLoading);
  const [outlineKnown, setOutlineKnown] = useState(() => !!outline);
  const sawOutlineLoad = useRef(false);
  useEffect(() => {
    if (outline || (sawOutlineLoad.current && !outlineLoading)) setOutlineKnown(true);
    if (outlineLoading) sawOutlineLoad.current = true;
  }, [outline, outlineLoading]);
  const { centred, startedCentred, dock, stageRef: stageInRef } = useCentreStage({
    isB,
    hasOutline: hasPlan,
    // A start that reaches the studio without its words (a remount, the history state lost) is still fresh once Ivy is at
    // work on a video with no plan yet.
    known: !!initialMessage || outlineKnown || (!hasPlan && studio.working),
    stage: studio.stage,
  });

  // Before an outline exists the chat lives in the CENTER canvas (story development).
  // On any other tab it has to move back to the left panel, or switching away from Outline
  // strands the user on a canvas with no way to talk to the agent.
  const chatInCenter = isB ? centred : activeTab === 'outline' && !outline;
  const chatPanelOpen = showChatPanel && !chatInCenter;
  useEffect(() => {
    if (!chatInCenter) setShowChatPanel(true);
  }, [chatInCenter]);

  // Direction B (beta only): one panel per side, and below 1600 px one side at a time; an open right panel folds Ivy.
  const roomForBothSides = useRoomForBothSides(isB);
  const [barSlot, setBarSlot] = useState<HTMLDivElement | null>(null);
  // A feedback card in Ivy's thread opens the storyboard at the page it was sent from.
  const [storyboardAsk, setStoryboardAsk] = useState<{ version: number }>();
  useEffect(() => {
    if (!isB) return;
    const onOpen = (e: Event) => {
      const { conversationId: id, version } = (e as CustomEvent).detail ?? {};
      if (id !== conversationId) return;
      if (typeof version === 'number') setStoryboardAsk({ version });
      setActiveTabState('storyboard');
    };
    window.addEventListener('storyboard-open', onOpen);
    return () => window.removeEventListener('storyboard-open', onOpen);
  }, [isB, conversationId]);
  // Chat panel expanded: 50% wide today; in B, Ivy widens in place on the left
  const [chatPanelExpanded, setChatPanelExpanded] = useState(false);
  const rightPanelOpen = showAudioPanel || showReviewsPanel;
  useEffect(() => {
    if (isB && !roomForBothSides && chatPanelOpen && rightPanelOpen) setShowChatPanel(false);
  }, [isB, roomForBothSides, chatPanelOpen, rightPanelOpen]);
  const openRightPanelB = (panel: 'audio' | 'reviews') => {
    dock();
    setChatPanelExpanded(false);
    const opening = panel === 'audio' ? !showAudioPanel : !showReviewsPanel;
    setShowAudioPanel(panel === 'audio' && opening);
    setShowReviewsPanel(panel === 'reviews' && opening);
    if (opening && !roomForBothSides && !phone) setShowChatPanel(false);
  };
  // Every view in the stage swaps with the same quick crossfade in B (Rahul, 31 Oct: smooth, professional motion).
  const bView = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: bFade() };
  // On a phone (D9): More's sheet holds what the desktop bar does beyond the view and the progress, the storyboard's
  // page menu included: the phone's bar has no room for it.
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreSlot, setMoreSlot] = useState<HTMLDivElement | null>(null);
  const openIvyB = () => {
    setShowChatPanel(true);
    if (!roomForBothSides) {
      setShowAudioPanel(false);
      setShowReviewsPanel(false);
    }
  };

  // Auto-derived context sent with every ivy-chat message (hidden from user in UI)
  const activeSlide = slides.find(s => s.id === currentSlideId);
  const ivyChatSystemContext = React.useMemo(
    () => workspaceContext({ conversationId, title: outline?.title, activeTab, activeSlide: activeSlide && { id: activeSlide.id, title: activeSlide.title } }),
    [conversationId, outline?.title, activeTab, activeSlide],
  );

  // Script view state removed - All Slides view moved to Narration tab

  // Transition state
  const [savingTransitions, setSavingTransitions] = useState<Record<string, boolean>>({});

  // Smart add modal state
  const [smartAddModalOpen, setSmartAddModalOpen] = useState(false);
  const [smartAddAfterSlideId, setSmartAddAfterSlideId] = useState<string | undefined>();
  const [isSmartAdding, setIsSmartAdding] = useState(false);

  // Filmstrip drag-and-drop state
  const [activeFilmstripDragId, setActiveFilmstripDragId] = useState<string | null>(null);
  // Read by the window Space shortcut, whose listener is not re-made when a drag starts.
  const filmstripDragRef = useRef<string | null>(null);
  filmstripDragRef.current = activeFilmstripDragId;

  const filmstripSensors = useSceneSortSensors();


  // Auto-open chat panel when outline gets generated (story dev → outline transition)
  const prevOutlineRef = useRef(outline);
  useEffect(() => {
    if (!prevOutlineRef.current && outline) {
      setShowChatPanel(true);
    }
    prevOutlineRef.current = outline;
  }, [outline]);

  // Trigger resize when panels change so slide scales properly
  useEffect(() => {
    // Small delay to allow CSS transitions to complete
    const timeoutId = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [chatPanelOpen, showAudioPanel]);

  // Fetch or build video manifest when entering video mode
  // Uses API to ensure manifest has proper audio data from database
  useEffect(() => {
    if (!isVideoMode || slides.length === 0) {
      setVideoManifest(null);
      return;
    }

    let cancelled = false;

    const loadManifest = async () => {
      try {
        // Always refresh manifest to get latest audio URLs
        // This rebuilds from the database ensuring audio URLs are current
        console.log('[VideoWorkspacePanels] Refreshing manifest to get latest audio...');
        const result = await videoManifestAPI.refreshManifest(conversationId, {
          apiBaseUrl: getApiServerUrl(),
        });

        if (cancelled) return;

        console.log('[VideoWorkspacePanels] Manifest refreshed:', {
          slideCount: result.manifest.slides.length,
          totalDuration: result.manifest.metadata.totalDuration,
          slidesWithAudio: result.manifest.slides.filter(s => s.audio?.url).length,
          firstSlideJsonPath: result.manifest.slides[0]?.jsonPath || 'none',
          apiBaseUrlUsed: getApiServerUrl(),
        });
        // The player reads the signed workspace CDN; let the project cookie land first so its
        // first requests are not cached 403s. Awaited after the refresh on purpose: the shell's
        // cookie effect runs after this child effect, so it has registered by now.
        await whenWorkspaceCdnReady(conversationId);
        if (cancelled) return;
        setVideoManifest(result.manifest);
      } catch (error) {
        console.error('[VideoWorkspacePanels] Failed to load/build manifest:', error);

        // Fallback to local build if API fails (e.g., network error)
        // This won't have audio data but at least shows the slides
        const workspaceSlides: WorkspaceSlide[] = slides.map(slide => {
          const slideConv = slideConversations.find(sc => sc.outlineSlideId === slide.id);
          return {
            id: slide.id,
            title: slide.title,
            childConversationId: slide.childConversationId,
            thumbnail: slide.thumbnail,
            duration: slide.duration,
            audioUrl: undefined,
            audioDuration: undefined,
            status: slideConv?.status || slide.status,
          };
        });

        const manifest = buildVideoManifest(workspaceSlides, {
          title: outline?.title || 'Untitled Video',
          baseUrl: getApiServerUrl(),
        });

        if (!cancelled) {
          console.log('[VideoWorkspacePanels] Fallback: Built manifest locally:', {
            slideCount: manifest.slides.length,
            totalDuration: manifest.metadata.totalDuration,
          });
          setVideoManifest(manifest);
        }
      }
    };

    loadManifest();

    return () => {
      cancelled = true;
    };
  }, [isVideoMode, conversationId, slides.length]); // Only re-fetch when slide count changes

  // Initialize audio state from manifest on load (for persistence)
  useEffect(() => {
    if (!videoManifest || !conversationId) return;

    // Initialize background music and narrator volume from manifest
    const { initializeAudioStateFromManifest } = useVideoWorkspaceStore.getState();
    initializeAudioStateFromManifest(conversationId, {
      backgroundMusic: videoManifest.backgroundMusic,
      audioMix: videoManifest.audioMix,
    });

    console.log('[VideoWorkspacePanels] Initialized audio state from manifest:', {
      hasBackgroundMusic: !!videoManifest.backgroundMusic,
      narratorVolume: videoManifest.audioMix?.narratorVolume ?? 1.0,
    });
  }, [videoManifest, conversationId]);

  // Preload narration data for instant tab switching
  // This runs in the background when outline is available, so switching to Narration tab is instant
  useEffect(() => {
    if (!isVideoMode || !outline || !conversationId) return;

    const { getNarrationCache, preloadNarrationData } = useVideoWorkspaceStore.getState();
    const existingCache = getNarrationCache(conversationId);

    // Preload if no cache or stale (> 5 minutes)
    const CACHE_TTL = 5 * 60 * 1000;
    if (!existingCache || (Date.now() - existingCache.lastFetchedAt) > CACHE_TTL) {
      console.log('[VideoWorkspacePanels] Preloading narration data for instant tab switching...');
      preloadNarrationData(conversationId);
    }
  }, [isVideoMode, outline, conversationId]);

  // Invalidate narration cache when scripts are updated (from any source)
  // This ensures Narration tab shows fresh data after chat-based script edits
  // BUT: Don't invalidate when narration tab is active - NarrationView manages its own updates
  useEffect(() => {
    if (!conversationId) return;

    const handleScriptsBatchUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: eventConvId } = customEvent.detail || {};

      // Only invalidate if the event is for our conversation
      if (!eventConvId || eventConvId === conversationId) {
        // Don't invalidate when narration tab is active - NarrationView handles its own updates
        // and invalidating causes a cascade of re-renders
        if (activeTab === 'narration') {
          console.log('[VideoWorkspacePanels] Scripts updated but narration tab active, skipping cache invalidation');
          return;
        }
        console.log('[VideoWorkspacePanels] Scripts updated, invalidating narration cache');
        useVideoWorkspaceStore.getState().invalidateNarrationCache(conversationId);
      }
    };

    window.addEventListener('scripts-batch-updated', handleScriptsBatchUpdated);
    return () => window.removeEventListener('scripts-batch-updated', handleScriptsBatchUpdated);
  }, [conversationId, activeTab]);

  // Invalidate narration cache when audio status changes (generation complete/failed)
  // This ensures Narration tab shows fresh audio status after TTS generation
  useEffect(() => {
    if (!conversationId) return;

    const handleAudioStatusChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { audioEvent, conversationId: eventConvId } = customEvent.detail || {};

      // Only invalidate on completion events for our conversation
      if (eventConvId && eventConvId !== conversationId) return;

      const status = audioEvent?.status;
      const type = audioEvent?.type;

      // Invalidate cache on audio completion events
      if (status === 'COMPLETED' || status === 'FAILED' || type === 'audio_generated' || type === 'audio_error' || type === 'audio_complete') {
        console.log('[VideoWorkspacePanels] Audio status changed, invalidating narration cache');
        useVideoWorkspaceStore.getState().invalidateNarrationCache(conversationId);
      }
    };

    window.addEventListener('audio-event', handleAudioStatusChange);
    return () => window.removeEventListener('audio-event', handleAudioStatusChange);
  }, [conversationId]);

  // Refresh manifest when audio is likely to have been updated
  // TODO: Wire this up to audio generation completion events
  const _refreshManifest = useCallback(async () => {
    if (!isVideoMode) return;

    try {
      console.log('[VideoWorkspacePanels] Refreshing manifest to get latest audio...');
      const result = await videoManifestAPI.refreshManifest(conversationId, {
        apiBaseUrl: getApiServerUrl(),
      });
      setVideoManifest(result.manifest);
      console.log('[VideoWorkspacePanels] Manifest refreshed:', {
        slidesWithAudio: result.manifest.slides.filter(s => s.audio?.url).length,
      });
    } catch (error) {
      console.error('[VideoWorkspacePanels] Failed to refresh manifest:', error);
    }
  }, [isVideoMode, conversationId]);

  // Refresh manifest when outline slide order changes (e.g., from outline panel reorder)
  // This ensures the slide viewer stays in sync when reordering from any panel
  // Uses debouncing to prevent multiple rapid refreshes during optimistic + server updates
  useEffect(() => {
    if (!isVideoMode || !outline?.slides) return;

    // Create a string representation of the current slide order
    const currentOrder = outline.slides.map(s => s.id).join(',');

    // On first render, just store the order without refreshing
    if (!prevOutlineOrderRef.current) {
      prevOutlineOrderRef.current = currentOrder;
      return;
    }

    // Skip during active reorder operations - handleReorderSlides will refresh after completion
    if (isReorderingRef.current) {
      prevOutlineOrderRef.current = currentOrder;
      return;
    }

    // If order changed (from outline panel or other source), refresh the manifest
    // Use debounce to prevent double refresh from optimistic + server updates
    if (currentOrder !== prevOutlineOrderRef.current) {
      prevOutlineOrderRef.current = currentOrder;

      // Clear any pending refresh
      if (manifestRefreshTimeoutRef.current) {
        clearTimeout(manifestRefreshTimeoutRef.current);
      }

      // Debounce the refresh by 300ms to let both optimistic and server updates settle
      manifestRefreshTimeoutRef.current = setTimeout(() => {
        console.log('[VideoWorkspacePanels] Outline order changed externally, refreshing manifest');
        _refreshManifest();
        manifestRefreshTimeoutRef.current = null;
      }, 300);
    }

    // Cleanup on unmount
    return () => {
      if (manifestRefreshTimeoutRef.current) {
        clearTimeout(manifestRefreshTimeoutRef.current);
      }
    };
  }, [isVideoMode, outline?.slides, _refreshManifest]);

  // Listen for slide updates from worker/chat and update video composition
  // In video mode, slides are in child conversations, so we need to check both:
  // 1. Direct match with parent conversation ID
  // 2. Match with any child conversation ID (for slides updated via slide chat)
  useEffect(() => {
    if (!isCompositionMode || !conversationId) return;

    const handleSlidesUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: updatedConvId, currentSlideVersions, outlineSlideId } = customEvent.detail || {};

      // Check for direct match with parent conversation
      const isDirectMatch = updatedConvId === conversationId;

      // Check for match with any child conversation (video mode)
      // Each slide has a childConversationId where its chat happens
      let matchedOutlineSlideId: string | null = null;

      if (!isDirectMatch) {
        // Check outline slides for matching child conversation
        const matchingOutlineSlide = outline?.slides?.find(
          (slide: any) => slide.slideConversationId === updatedConvId
        );
        if (matchingOutlineSlide) {
          matchedOutlineSlideId = matchingOutlineSlide.id;
        }

        // Also check slides array (for completed slides with childConversationId)
        if (!matchedOutlineSlideId) {
          const matchingSlide = slides.find(
            slide => slide.childConversationId === updatedConvId
          );
          if (matchingSlide) {
            matchedOutlineSlideId = matchingSlide.id;
          }
        }
      }

      // For direct match with outlineSlideId (from MCP slide_synced events),
      // use the outlineSlideId to target the specific slide
      if (isDirectMatch && outlineSlideId && !matchedOutlineSlideId) {
        matchedOutlineSlideId = outlineSlideId;
      }

      const isChildMatch = matchedOutlineSlideId !== null;
      const shouldProcess = isDirectMatch || isChildMatch;

      if (!shouldProcess) return;

      console.log('[VideoWorkspacePanels] Slide updated via SSE, invalidating in composition:', {
        updatedConvId,
        conversationId,
        isDirectMatch,
        isChildMatch,
        matchedOutlineSlideId,
        outlineSlideId,
        currentSlideVersions,
      });

      // Invalidate the updated slide(s) in the composition and seek to end. Studio B never seeks here: a scene that
      // syncs leaves the stage and the playhead where they are.
      const showAtEnd = !studioBOn();
      if (compositionRef.current) {
        if (matchedOutlineSlideId) {
          // Targeted slide update - invalidate the specific slide
          console.log('[VideoWorkspacePanels] Invalidating slide:', matchedOutlineSlideId);
          if (!heldByB(matchedOutlineSlideId)) compositionRef.current.invalidateSlide(matchedOutlineSlideId);
          // Seek to end after invalidation completes (show completed state) — but never seek while
          // the user is actively playing; the content refreshes in place, we just don't move the playhead.
          if (showAtEnd) setTimeout(() => {
            if (useVideoWorkspaceStore.getState().playerState.isPlaying) return;
            compositionRef.current?.goToSlidePreview(matchedOutlineSlideId);
          }, 300);
        } else if (currentSlideVersions) {
          // Direct update with version info - invalidate all listed slides
          const slideIds = Object.keys(currentSlideVersions);
          for (const slideId of slideIds) {
            if (!heldByB(slideId)) compositionRef.current.invalidateSlide(slideId);
          }
          // Seek to end of first invalidated slide after invalidation completes (skip while playing)
          if (showAtEnd && slideIds.length > 0) {
            setTimeout(() => {
              if (useVideoWorkspaceStore.getState().playerState.isPlaying) return;
              compositionRef.current?.goToSlidePreview(slideIds[0]);
            }, 300);
          }
        }
      }

      // Refresh manifest to get latest slide data/audio/duration
      await _refreshManifest();
    };

    window.addEventListener('slides-updated', handleSlidesUpdated);
    return () => window.removeEventListener('slides-updated', handleSlidesUpdated);
  }, [isCompositionMode, conversationId, outline, slides, _refreshManifest]);

  // Listen for audio generation complete events
  useEffect(() => {
    if (!isCompositionMode || !conversationId) return;

    const handleAudioEvent = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: updatedConvId, audioEvent } = customEvent.detail || {};

      if (updatedConvId !== conversationId) return;
      if (audioEvent?.type !== 'audio_complete') return;

      console.log('[VideoWorkspacePanels] Audio generated, refreshing manifest:', {
        slideId: audioEvent.slideId,
      });

      // Invalidate the slide that got new audio and seek to end. Studio B never seeks here (see slides-updated).
      if (compositionRef.current && audioEvent.slideId) {
        if (!heldByB(audioEvent.slideId)) compositionRef.current.invalidateSlide(audioEvent.slideId);
        // Seek to end after invalidation completes (show completed state) — skip while playing
        if (!studioBOn()) setTimeout(() => {
          if (useVideoWorkspaceStore.getState().playerState.isPlaying) return;
          compositionRef.current?.goToSlidePreview(audioEvent.slideId);
        }, 300);
      }

      // Refresh manifest to get new audio duration
      await _refreshManifest();
    };

    window.addEventListener('audio-event', handleAudioEvent);
    return () => window.removeEventListener('audio-event', handleAudioEvent);
  }, [isCompositionMode, conversationId, _refreshManifest]);

  // Listen for manifest-updated events (from MCP tools like build_video_manifest via Redis → SSE)
  // Handled here (not in Shell) because setVideoManifest state lives in this component
  useEffect(() => {
    if (!isVideoMode || !conversationId) return;

    const handleManifestUpdated = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail.conversationId !== conversationId) return;

      console.log('[VideoWorkspacePanels] Received manifest-updated event, refreshing manifest...');
      await _refreshManifest();
    };

    window.addEventListener('manifest-updated', handleManifestUpdated);
    return () => window.removeEventListener('manifest-updated', handleManifestUpdated);
  }, [isVideoMode, conversationId, _refreshManifest]);

  // Track previous generating state to detect when generation completes
  const prevGeneratingRef = useRef(isOutlineGenerating);

  // Auto-open chat panel when outline generation completes
  useEffect(() => {
    // Detect transition from generating to not generating with outline available
    if (prevGeneratingRef.current && !isOutlineGenerating && outline) {
      console.log('📋 VideoWorkspacePanels - Outline generated, opening chat panel');
      setShowChatPanel(true);
    }
    prevGeneratingRef.current = isOutlineGenerating;
  }, [isOutlineGenerating, outline]);

  // Auto-open chat panel when outline generation fails (AI responded without outline)
  useEffect(() => {
    if (outlineNeedsInput) {
      console.log('📋 VideoWorkspacePanels - Outline needs input, opening chat panel');
      setShowChatPanel(true);
    }
  }, [outlineNeedsInput]);

  // Track if we've already auto-switched to studio (to avoid overriding user's manual tab choice)
  const hasAutoSwitchedToStudioRef = useRef(false);

  // Reset auto-switch ref when conversation changes (prevents stale behavior between workspaces)
  useEffect(() => {
    hasAutoSwitchedToStudioRef.current = false;
    setActiveTabState(resolveInitialTab(initialTab, creationMode)); // Also reset to the deep-linked (or default) tab for new conversations
  }, [conversationId, initialTab]);

  // A footage project starts in the story chat (its mode is set by the kickoff); once it has a
  // storyboard or an outline it moves to the Storyboard, which replaces the Outline for it.
  useEffect(() => {
    if (!isB && isFootageMode(creationMode) && activeTab === 'outline' && (hasStoryboard || outline)) {
      setActiveTabState('storyboard');
    }
  }, [isB, creationMode, activeTab, hasStoryboard, outline]);

  // Direction B (beta only): where a video opens when the link names no tab. One rule (landingTab), applied only while the
  // tab has never left its default and only once the storyboard check and the outline have both loaded, so the same video
  // opens on the same tab whichever of the two arrives first. Until then the view switch and the stage stay empty; a load
  // that never settles (a hung fetch, an outline load never seen) opens on what it has after LANDING_WAIT_MS. It sits
  // after the reset above, so on mount its move lands last.
  const [tabMoved, setTabMoved] = useState(false);
  useEffect(() => {
    if (activeTab !== 'outline') setTabMoved(true);
  }, [activeTab]);
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    if (!isB || initialTab || tabMoved) return;
    const timer = setTimeout(() => setWaitedOut(true), LANDING_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isB, initialTab, tabMoved]);
  const planScenes = outline?.slides.filter((s) => !s.isDeleted) ?? [];
  const landing = isB && !initialTab && ((storyboardKnown && outlineKnown) || waitedOut) ? landingTab({
    creationMode,
    hasStoryboard,
    hasOutline: !!outline,
    keepsOwnScreens: ownScreens,
    outlineDone: outline?.status === 'complete' || outline?.status === 'completed' || planScenes.some((s) => s.generationStatus === 'completed'),
    videoReady: planScenes.length > 0 && planScenes.every((s) => s.generationStatus === 'completed'),
  }) : null;
  const landingPending = isB && !initialTab && !tabMoved && activeTab === 'outline' && landing !== 'outline';
  useEffect(() => {
    if (landingPending && landing) setActiveTabState(landing);
  }, [landingPending, landing]);

  // Auto-switch to studio tab ONCE when at least one slide is COMPLETED
  // This prevents switching while slides are still generating
  // After that, user can freely switch between tabs
  useEffect(() => {
    // Skip if we've already auto-switched; Direction B opens by landingTab instead.
    if (isB || hasAutoSwitchedToStudioRef.current) return;

    // Check if we should auto-switch to studio tab
    // Only switch when at least one slide is actually completed (not just generating)
    const hasCompletedSlide = slides.some(s => s.status === 'completed');
    const shouldSwitchToStudio =
      // Outline generation is complete (accept both legacy 'complete' and new 'completed')
      outline?.status === 'complete' || outline?.status === 'completed' ||
      // OR at least one slide has been generated
      hasCompletedSlide;

    if (shouldSwitchToStudio && activeTab === 'outline') {
      console.log('🎬 VideoWorkspacePanels - Auto-switching to studio tab (one-time):', {
        outlineStatus: outline?.status,
        slideCount: slides.length,
        completedSlides: slides.filter(s => s.status === 'completed').length,
      });
      setActiveTab('studio');
      hasAutoSwitchedToStudioRef.current = true;
    }
  }, [isB, outline?.status, slides, activeTab, setActiveTab]);

  // NOTE: Audio slicing removed — audio-first videos now use continuous master narration
  // at the video level. No per-slide audio slicing needed.

  // Auto-sync chat context with current view (tab and slide selection)
  useEffect(() => {
    // Skip if not in video mode
    if (!isVideoMode) return;

    // Tab-based context switching - always follow the tab
    if (activeTab === 'outline' || activeTab === 'storyboard' || activeTab === 'scenes') {
      // Outline / Storyboard / Scenes tab → use outline context (parent conversation)
      setActiveChatContext('outline');
      setActiveConversation(conversationId);
    } else if (activeTab === 'narration') {
      // Narration tab → use narration context (chat-based script editing)
      setActiveChatContext('narration');
      setActiveConversation(conversationId);
    } else if (activeTab === 'studio') {
      // For studio tab, preserve broadcast mode if selected
      if (activeChatContext === 'broadcast') return;

      // Studio tab → use current slide's conversation
      if (currentSlideId) {
        // First try slideConversations (populated during generation), then fall back to slides array
        // (populated from outline on page load)
        const slideConv = slideConversations.find(
          sc => sc.outlineSlideId === currentSlideId
        );

        if (slideConv?.id) {
          // Slide has a conversation in slideConversations → switch to it
          setActiveChatContext(slideConv.id);
          setActiveConversation(slideConv.id);
        } else {
          // Fallback: check slides array (populated from outline with childConversationId)
          const slideFromArray = slides.find(s => s.id === currentSlideId);
          if (slideFromArray?.childConversationId) {
            setActiveChatContext(slideFromArray.childConversationId);
            setActiveConversation(slideFromArray.childConversationId);
          }
        }
      }
      // If slide doesn't have conversation yet (generating), keep previous context
      // Don't change anything - user stays on whatever they were chatting with
    }
  // Note: activeChatContext removed from deps to prevent infinite loops when switching contexts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentSlideId, slideConversations, conversationId, isVideoMode, setActiveChatContext, setActiveConversation, slides]);

  // Version switching state
  const [isSwitchingVersion, setIsSwitchingVersion] = useState(false);

  // ============================================================================
  // SLIDE MANAGEMENT: Add, Delete, Reorder
  // ============================================================================

  // Get conversation-scoped setter for outline
  const { setOutlineForConversation } = useVideoWorkspaceStore.getState();

  // Handler to open the smart add modal
  const handleOpenSmartAdd = useCallback((afterSlideId?: string) => {
    setSmartAddAfterSlideId(afterSlideId);
    setSmartAddModalOpen(true);
  }, []);

  // Handler for smart add submission - creates placeholder and triggers outline planner
  const handleSmartAddSubmit = useCallback(async (data: SmartAddSlideData) => {
    if (!conversationId || !outline) return;

    setIsSmartAdding(true);
    try {
      // 1. Calculate position
      const activeSlides = outline.slides.filter((s: any) => !s.isDeleted);
      let position = activeSlides.length + 1;
      if (smartAddAfterSlideId) {
        const afterIndex = activeSlides.findIndex(s => s.id === smartAddAfterSlideId);
        if (afterIndex !== -1) position = afterIndex + 2;
      }

      // 2. Create placeholder slide with user's instructions
      const slideData: any = {
        title: 'Generating...',
        description: data.instructions,
        keyPoints: [],
      };
      // Only set layout if user selected a specific one (not "Smart")
      if (data.layoutId) {
        slideData.layoutId = data.layoutId;
        slideData.layoutName = data.layoutName;
      }

      const result = await outlineAPI.addSlide(conversationId, { position, slideData });

      // 3. Update store
      setOutlineForConversation(conversationId, result.outline);

      // 4. Close modal
      setSmartAddModalOpen(false);

      // 5. Build message for outline planner
      const newSlideId = result.slide.id;
      let message = `Generate content for the new slide (${newSlideId}) at position ${position}:\n\n${data.instructions}`;

      // If user selected a specific layout, mention it; otherwise AI picks
      if (data.layoutId) {
        message += `\n\nUse layout: ${data.layoutName} (${data.layoutId})`;
      } else {
        message += `\n\nSelect the most appropriate layout based on the content.`;
      }

      // 6. Send message to trigger outline planner
      await unifiedMessageService.sendMessage({
        conversationId,
        content: message,
        context: { source: 'outline', outlineAction: 'edit' },
      });

      toast.success('Generating slide content...');
    } catch (error) {
      console.error('Failed to smart add slide:', error);
      toast.error('Failed to add slide');
    } finally {
      setIsSmartAdding(false);
    }
  }, [conversationId, outline, smartAddAfterSlideId, setOutlineForConversation]);

  // Delete confirmation state
  const [deleteConfirmSlide, setDeleteConfirmSlide] = useState<{ id: string; title: string } | null>(null);

  const handleRequestDeleteSlide = useCallback((slideId: string, slideTitle: string) => {
    setDeleteConfirmSlide({ id: slideId, title: slideTitle });
  }, []);

  // Handler to delete a slide
  const handleDeleteSlide = useCallback(async (slideId: string) => {
    if (!conversationId || !outline) return;

    try {
      const result = await outlineAPI.deleteSlide(conversationId, slideId);

      // Update store with new outline from API response
      setOutlineForConversation(conversationId, result.outline);

      // If we deleted the current slide, select the first available slide
      if (slideId === currentSlideId) {
        const remainingSlides = result.outline.slides.filter((s: any) => !s.isDeleted);
        if (remainingSlides.length > 0) {
          setCurrentSlideId(remainingSlides[0].id);
        }
      }

      // Refresh manifest to update video playback order
      try {
        await videoManifestAPI.refreshManifest(conversationId);
      } catch (manifestError) {
        console.warn('Failed to refresh manifest after delete:', manifestError);
      }

      toast.success('Slide deleted', {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const restoreResult = await outlineAPI.restoreSlide(conversationId, slideId);
              setOutlineForConversation(conversationId, restoreResult.outline);
              // Refresh manifest after restore too
              await videoManifestAPI.refreshManifest(conversationId);
              toast.success('Slide restored');
            } catch (err) {
              toast.error('Failed to restore slide');
            }
          },
        },
        duration: 8000,
      });
    } catch (error: any) {
      console.error('Failed to delete slide:', error);
      const message = error.response?.data?.error || 'Failed to delete slide';
      toast.error(message);
    }
  }, [conversationId, outline, currentSlideId, setCurrentSlideId, setOutlineForConversation]);

  const handleConfirmDeleteSlide = useCallback(() => {
    if (!deleteConfirmSlide) return;
    handleDeleteSlide(deleteConfirmSlide.id);
    setDeleteConfirmSlide(null);
  }, [deleteConfirmSlide, handleDeleteSlide]);

  // Handler to restore a deleted slide (puts it at the end by default)
  const handleRestoreDeletedSlide = useCallback(async (slideId: string) => {
    if (!conversationId || !outline) return;

    try {
      // Get active slides count to determine position (restore to end)
      const activeSlides = outline.slides.filter(s => !s.isDeleted);
      const restoreResult = await outlineAPI.restoreSlide(conversationId, slideId, {
        position: activeSlides.length + 1,
      });
      setOutlineForConversation(conversationId, restoreResult.outline);

      // Refresh manifest after restore
      try {
        await videoManifestAPI.refreshManifest(conversationId);
      } catch (manifestError) {
        console.warn('Failed to refresh manifest after restore:', manifestError);
      }

      toast.success('Slide restored');
    } catch (error: any) {
      console.error('Failed to restore slide:', error);
      const message = error.response?.data?.error || 'Failed to restore slide';
      toast.error(message);
    }
  }, [conversationId, outline, setOutlineForConversation]);

  // Handler to reorder slides
  const handleReorderSlides = useCallback(async (fromIndex: number, toIndex: number) => {
    if (!conversationId || !outline) return;

    // Mark that we're reordering to prevent the auto-refresh effect from triggering
    isReorderingRef.current = true;

    // Optimistic update: immediately reorder in UI for responsive drag-and-drop
    reorderOutlineSlides(fromIndex, toIndex);

    try {
      const result = await outlineAPI.reorderSlides(conversationId, { fromIndex, toIndex });

      // Update store with server response (has renumbered IDs)
      setOutlineForConversation(conversationId, result.outline);

      // Update the order ref to match the server's renumbered IDs
      prevOutlineOrderRef.current = result.outline.slides.map((s: any) => s.id).join(',');

      // Refresh manifest to update video playback order AND update local state
      try {
        const manifestResult = await videoManifestAPI.refreshManifest(conversationId, {
          apiBaseUrl: getApiServerUrl(),
        });
        // Update local manifest state so slide viewer reflects new order immediately
        setVideoManifest(manifestResult.manifest);
        console.log('[VideoWorkspacePanels] Manifest updated after reorder:', {
          slideCount: manifestResult.manifest.slides.length,
          slideOrder: manifestResult.manifest.slides.map(s => s.id).join(', '),
        });
      } catch (manifestError) {
        console.warn('Failed to refresh manifest after reorder:', manifestError);
      }
    } catch (error) {
      console.error('Failed to reorder slides:', error);
      toast.error('Failed to reorder slides');
      // Revert optimistic update on failure by refreshing from server
      try {
        const outlineResult = await outlineAPI.get(conversationId);
        if (outlineResult) {
          setOutlineForConversation(conversationId, outlineResult);
          // Update the order ref to match the reverted state
          prevOutlineOrderRef.current = outlineResult.slides.map((s: any) => s.id).join(',');
        }
      } catch (revertError) {
        console.error('Failed to revert outline after reorder error:', revertError);
      }
    } finally {
      // Always clear the reordering flag
      isReorderingRef.current = false;
    }
  }, [conversationId, outline, setOutlineForConversation, reorderOutlineSlides]);

  // Generate stable IDs for filmstrip slides - use childConversationId (stable UUID) for completed slides,
  // fall back to slide.id for pending slides. This prevents key collisions when server renumbers.
  const getStableSlideId = useCallback((slide: Slide) => slide.childConversationId || slide.id, []);

  // Filmstrip drag handlers
  const handleFilmstripDragStart = useCallback((event: DragStartEvent) => {
    setActiveFilmstripDragId(event.active.id as string);
  }, []);

  const handleFilmstripDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveFilmstripDragId(null);

    if (over && active.id !== over.id && slides.length > 0) {
      // Find indices using stable IDs
      const oldIndex = slides.findIndex((s) => getStableSlideId(s) === active.id);
      const newIndex = slides.findIndex((s) => getStableSlideId(s) === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        handleReorderSlides(oldIndex, newIndex);
      }
    }
  }, [slides, handleReorderSlides, getStableSlideId]);

  // Computed values
  const currentSlide = slides.find((s) => s.id === currentSlideId);
  const currentIndex = slides.findIndex((s) => s.id === currentSlideId);

  // Get current slide's generation status for main canvas placeholder
  const currentSlideConv = slideConversations.find(sc => sc.outlineSlideId === currentSlideId);
  const currentSlideStatus = currentSlideConv?.status || currentSlide?.status || 'completed';
  const currentSlideThinking = currentSlideConv?.generatingThinking;

  // Get current outline slide (for rendering OutlineSlideCard in slide viewer)
  const currentOutlineSlide = outline?.slides.find(s => s.id === currentSlideId && !s.isDeleted);
  const activeOutlineSlides = outline?.slides.filter(s => !s.isDeleted) || [];

  // Determine if the current slide has NEVER been generated before
  // (no slideConversationId means it was never generated)
  // Only show OutlineSlideCard for never-generated slides; once generated, always show preview
  const slideNeverGenerated = currentOutlineSlide && !currentOutlineSlide.slideConversationId;
  const plannedSlide = currentSlideId && slideNeverGenerated && (currentSlideStatus === 'pending' || currentSlideStatus === 'generating' || currentSlideStatus === 'failed') ? currentOutlineSlide : null;

  // Direction B (beta only): playback holds at a scene still building, showing its plan over the player, and carries on
  // when the scene lands; until the first scene lands the stage follows the build, then the first one takes it once.
  const [, setWaitingSceneId] = useState<string | null>(null);
  const [manifestLoads, setManifestLoads] = useState(0);
  const followBuild = useRef<boolean | null>(null);
  const sceneBuilt = useCallback((id: string) => (studio.scenes.find((x) => x.id === id)?.status ?? 'built') === 'built', [studio.scenes]);
  const manifestPath = useCallback((id: string) => videoManifest?.slides.find((x) => x.id === id)?.jsonPath ?? '', [videoManifest]);
  // Carrying on keeps the plan up until the player plays the scene. A player that went into the scene while it waited
  // holds a stand-in for it and does nothing when sent there again, so it gets the scene again in place first.
  const [resumingId, setResumingId] = useState<string | null>(null);
  const endResume = useCallback(() => {
    if (resumeRef.current) window.clearTimeout(resumeRef.current.timer);
    resumeRef.current = null;
    setResumingId(null);
  }, []);
  const stopWaiting = useCallback(() => {
    waitRef.current = null;
    setWaitingSceneId(null);
    endResume();
  }, [endResume]);
  const handleTransitionStarted = useCallback((to: string) => {
    if (waitRef.current?.id === to || sceneBuilt(to)) return;
    waitRef.current = { id: to, path: manifestPath(to), loaded: false };
    compositionRef.current?.pause();
    setWaitingSceneId(to);
    setCurrentSlideId(to);
  }, [sceneBuilt, manifestPath, setCurrentSlideId]);
  const handleManifestLoaded = useCallback(() => {
    if (waitRef.current && sceneBuilt(waitRef.current.id)) waitRef.current.loaded = true;
    setManifestLoads((n) => n + 1);
  }, [sceneBuilt]);
  useEffect(() => {
    const w = waitRef.current;
    if (!w || !canResume({ built: sceneBuilt(w.id), pathAtWait: w.path, pathNow: manifestPath(w.id), loadedSinceBuilt: w.loaded })) return;
    stopWaiting();
    // In a master-narration video the player's clock is the narration element, and its play() won't restart one that
    // has ended, or that a manifest refresh swapped for a new one at 0:00 while it waited. A seek to the player's own
    // time puts the narration where the picture is; the player only moves it once the scene's timeline is ready.
    const reloading = compositionState.currentSlideId === w.id;
    const at = compositionState.currentSlideId ? compositionState.currentTime : null;
    resumeRef.current = { id: w.id, reloading, at, timer: window.setTimeout(endResume, RESUME_WAIT_MS) };
    setResumingId(w.id);
    if (!reloading && at != null) compositionRef.current?.seek(at);
    compositionRef.current?.invalidateSlide(w.id);
    if (reloading) return;
    compositionRef.current?.goToSlide(w.id);
    compositionRef.current?.play();
  }, [sceneBuilt, manifestPath, manifestLoads, stopWaiting, endResume, compositionState.currentSlideId, compositionState.currentTime]);
  const handleSlideReloaded = useCallback((id: string) => {
    const r = resumeRef.current;
    if (!r?.reloading || r.id !== id) return;
    r.reloading = false;
    if (r.at != null) compositionRef.current?.seek(r.at);
    compositionRef.current?.play();
  }, []);
  useEffect(() => {
    const r = resumeRef.current;
    if (r && !r.reloading && compositionState.isPlaying && compositionState.currentSlideId === r.id) endResume();
  }, [compositionState.isPlaying, compositionState.currentSlideId, endResume]);
  useEffect(() => endResume, [endResume]);
  useEffect(() => {
    if (!isB || buildScenes.length === 0) return;
    if (followBuild.current === null) followBuild.current = !buildScenes.some((x) => x.status === 'built');
    if (!followBuild.current) return;
    const landed = buildScenes.find((x) => x.status === 'built');
    if (landed) followBuild.current = false;
    const next = landed ?? pickFocusScene(buildScenes);
    if (next && next.id !== currentSlideId) setCurrentSlideId(next.id);
  }, [isB, buildScenes, currentSlideId, setCurrentSlideId]);
  // A scene the person picks ends the following and any hold.
  const picked = <A extends unknown[]>(fn: (...args: A) => void) => (isB ? (...args: A) => { followBuild.current = false; stopWaiting(); fn(...args); } : fn);
  const stageScene = isB ? buildScenes.find((x) => x.id === currentSlideId) : undefined;
  const stageUnbuilt = isB && !!currentSlideId && (!!plannedSlide || (!!stageScene && stageScene.status !== 'built') || resumingId === currentSlideId);
  const stageFailed = currentSlideStatus === 'failed' || stageScene?.status === 'needs-attention';
  const skipScene = () => {
    const next = slides[slides.findIndex((x) => x.id === currentSlideId) + 1];
    followBuild.current = false;
    stopWaiting();
    if (!next) return;
    compositionRef.current?.goToSlide(next.id);
    setCurrentSlideId(next.id);
    compositionRef.current?.play();
  };
  const buildFrame = stageUnbuilt
    ? {
        number: activeOutlineSlides.findIndex((x) => x.id === currentSlideId) + 1,
        total: activeOutlineSlides.length,
        title: currentOutlineSlide?.title ?? stageScene?.title ?? '',
        sketch: currentOutlineSlide?.description,
        draft: studio.scenes.find((x) => x.id === currentSlideId)?.thumbnail,
        failed: stageFailed,
        onSkip: stageFailed ? skipScene : undefined,
      }
    : undefined;

  // For video mode slides, use the child conversation ID for API calls
  // Each video slide is a separate child conversation with slide-001
  const effectiveConversationId = mode === 'video' && currentSlide?.childConversationId
    ? currentSlide.childConversationId
    : conversationId;

  const { selectScene: handleSlideSelect, prevScene: handlePrevSlide, nextScene: handleNextSlide } = useSceneNavigation({
    slides,
    currentSlideId,
    isVideoMode,
    isCompositionMode,
    compositionRef,
    setCurrentSlideId,
    setActiveChatContext,
    setActiveConversation,
  });

  // Player controls - work with both composition and single-slide modes
  const handlePlayPause = () => {
    if (isCompositionMode && compositionRef.current) {
      compositionRef.current.togglePlay();
    } else {
      previewRef.current?.togglePlay();
    }
  };

  const handleRestart = () => {
    if (isCompositionMode && compositionRef.current) {
      compositionRef.current.seek(0);
      compositionRef.current.play();
    } else {
      previewRef.current?.restart();
    }
  };

  // The reopen summary's "Watch from the start": the studio, then the video from 0:00 once its player has the manifest.
  const [playFromStart, setPlayFromStart] = useState(false);
  useEffect(() => {
    if (!isB) return;
    const onPlay = (e: Event) => {
      if ((e as CustomEvent).detail?.conversationId !== conversationId) return;
      setActiveTabState('studio');
      setPlayFromStart(true);
    };
    window.addEventListener('studio-play-from-start', onPlay);
    return () => window.removeEventListener('studio-play-from-start', onPlay);
  }, [isB, conversationId]);
  useEffect(() => {
    if (!playFromStart || activeTab !== 'studio' || !videoManifest || !compositionRef.current) return;
    setPlayFromStart(false);
    handleRestart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playFromStart, activeTab, videoManifest]);

  // Composition state change handler
  const handleCompositionStateChange = useCallback((state: CompositionPlaybackState) => {
    setCompositionState(state);

    // Sync current slide with composition (but skip during suppression to prevent resetting to first slide)
    if (state.currentSlideId && !suppressSlideStartRef.current) {
      followCanvasScene(state.currentSlideId);
    }
  }, [followCanvasScene]);

  // Track if we've done initial seek-to-end for composition mode (only false on actual page load)
  const initialSeekDoneRef = useRef(false);

  // Effect to seek to end on initial composition load (show completed slide state)
  // This only runs ONCE on actual page load, not when returning from edit mode or switching versions
  useEffect(() => {
    if (isCompositionMode && compositionRef.current && compositionState.currentSlideId) {
      // Only do this once on initial load, not during playback, version switching, or after user interactions
      if (!initialSeekDoneRef.current && !compositionState.isPlaying && compositionState.currentTime === 0 && !isSwitchingVersion) {
        console.log('[VideoWorkspacePanels] Initial load - seeking to end of slide:', compositionState.currentSlideId);
        compositionRef.current.goToSlidePreview(compositionState.currentSlideId);
        initialSeekDoneRef.current = true;
      }
    }
    // NOTE: Do NOT reset initialSeekDoneRef when leaving composition mode
    // We only want this to fire on actual page load, not when returning from edit mode
  }, [isCompositionMode, compositionState.currentSlideId, compositionState.isPlaying, compositionState.currentTime, isSwitchingVersion]);

  // Effect to handle pending return to composition mode after save/cancel completes
  // When edit mode changes to false and we came from composition, switch back to composition mode
  // This handles both explicit exits (save/cancel) and implicit exits (asset replacement reload)
  useEffect(() => {
    // Check if edit mode just exited and we need to return to composition
    const shouldReturnToComposition = !isEditMode && (
      pendingReturnToComposition.current !== null ||
      enteredEditFromComposition.current
    );

    if (shouldReturnToComposition) {
      const action = pendingReturnToComposition.current || 'implicit';

      // If we're waiting for save to complete, DON'T switch modes yet
      // The return will be triggered by onSaveComplete callback instead
      if (waitingForSaveComplete.current && action === 'save') {
        console.log('[VideoWorkspacePanels] Edit mode exited, but waiting for save to complete before returning to composition');
        return;
      }

      console.log('[VideoWorkspacePanels] Edit mode exited, returning to composition mode', {
        pendingAction: action,
        cameFromComposition: enteredEditFromComposition.current
      });

      // Mark slide for invalidation if we saved or had an implicit exit (asset replacement)
      // Can't invalidate now because composition isn't mounted - happens in useEffect below
      const shouldInvalidate = action === 'save' || action === 'implicit';
      if (shouldInvalidate && currentSlideId) {
        console.log('[VideoWorkspacePanels] Marking slide for invalidation after composition mounts:', currentSlideId);
        pendingSlideInvalidation.current = currentSlideId;
      }

      // Suppress onSlideStart during composition remount to prevent resetting to first slide
      suppressSlideStartRef.current = true;

      // Return to composition mode
      setCompositionMode(true);

      // Clear the flags
      pendingReturnToComposition.current = null;
      enteredEditFromComposition.current = false;
    }
  }, [isEditMode, currentSlideId]);

  // Effect to invalidate slide cache after composition mode is re-enabled
  // Note: With initialSlideId prop, the composition now starts on the correct slide directly
  // We just need to invalidate the cache to get fresh content after edit
  useEffect(() => {
    if (isCompositionMode && compositionRef.current) {
      // Check if we have a pending slide invalidation
      if (pendingSlideInvalidation.current) {
        const slideToInvalidate = pendingSlideInvalidation.current;
        console.log('[VideoWorkspacePanels] Composition mounted with initialSlideId, invalidating cache for:', slideToInvalidate);

        // Invalidate the slide cache (clears cache so it reloads with new content)
        // Note: With initialSlideId, the composition already loaded the correct slide
        // This just ensures the cache is cleared for future visits to this slide
        compositionRef.current.invalidateSlide(slideToInvalidate);

        // Clear the pending invalidation
        pendingSlideInvalidation.current = null;

        // Clear the suppression flag after a short delay
        setTimeout(() => {
          suppressSlideStartRef.current = false;
          console.log('[VideoWorkspacePanels] Cleared suppressSlideStart flag');
        }, 200);
      } else if (suppressSlideStartRef.current) {
        // No pending invalidation but we're suppressing - clear after a short delay
        setTimeout(() => {
          suppressSlideStartRef.current = false;
          console.log('[VideoWorkspacePanels] Cleared suppressSlideStart flag (no invalidation)');
        }, 500);
      }
    }
  }, [isCompositionMode]);

  // Handle entering edit mode from composition
  const handleEnterEditFromComposition = useCallback(() => {
    // Mark that we came from composition mode - this ensures we return when edit mode exits
    // (even if exit is triggered by asset replacement/reload, not just save/cancel)
    enteredEditFromComposition.current = true;

    // Pause composition and remember position
    if (compositionRef.current) {
      compositionRef.current.pause();
      setReturnPosition(compositionState.currentTime);
    }

    // Switch to single-slide mode first
    setCompositionMode(false);

    // Wait longer for iframe to fully load and initialize before toggling edit mode
    // The iframe needs time to: 1) load HTML, 2) initialize viewer, 3) load slide data
    // Don't set setEditMode(true) here - wait for iframe to confirm via state callback
    setTimeout(() => {
      console.log('[VideoWorkspacePanels] Toggling edit mode after composition exit');
      previewRef.current?.toggleEditMode();
      // Edit mode state will be set by onPlayerStateChange when iframe confirms
    }, 500);
  }, [compositionState.currentTime]);

  // Note: handleExitEditToComposition logic is now in the useEffect above
  // that watches isEditMode and pendingReturnToComposition

  // Edit mode controls - handle composition mode switching
  const handleEnterEditMode = () => {
    if (isCompositionMode) {
      // Coming from composition mode
      handleEnterEditFromComposition();
    } else {
      // Already in single-slide mode - clear the flag to avoid false return to composition
      enteredEditFromComposition.current = false;
      previewRef.current?.toggleEditMode();
      setEditMode(true);
    }
    // Track which slide is being edited (for edit isolation)
    setEditingSlide(conversationId, currentSlideId);
  };

  const handleSaveEdit = () => {
    if (isVideoMode && !isCompositionMode) {
      // Mark that we want to return to composition mode after save completes
      // The actual switch happens when onSaveComplete is called (after API save finishes)
      pendingReturnToComposition.current = 'save';
      waitingForSaveComplete.current = true;
      console.log('[VideoWorkspacePanels] handleSaveEdit - waiting for save to complete before returning to composition');
    }
    previewRef.current?.finishEdit();
    // Clear editing slide tracking
    setEditingSlide(null, null);
    // IMPORTANT: Do NOT call triggerPreviewRefresh() or invalidateSlideCache() here.
    // finishEdit() sends the save command to the iframe synchronously.
    // If we trigger a preview refresh now, it changes the React key and remounts
    // the component, destroying the iframe before the save completes.
    // Pending updates and refresh are deferred to handleSaveComplete().
    // Don't immediately switch modes - wait for onSaveComplete callback after API save finishes
  };

  const handleCancelEdit = () => {
    if (isVideoMode && !isCompositionMode) {
      // Only set the flag if there are no unsaved changes (no confirmation dialog will be shown)
      // If there are changes, GsapSlidePreviewEnhanced will show a confirmation dialog
      // and we'll listen for the editExitConfirmed event below
      if (!editToolbarState?.canUndo) {
        pendingReturnToComposition.current = 'cancel';
      }
    }
    previewRef.current?.cancelEdit();
    // Clear editing slide tracking
    setEditingSlide(null, null);
    clearPendingSlideUpdate();
    // Seek to end to show completed slide state after canceling edit
    setPreviewSeekToEnd(true);
    // Don't immediately switch modes - wait for iframe to confirm exit edit mode
  };

  // Edit toolbar handlers
  const handleUndo = () => previewRef.current?.undo();
  const handleRedo = () => previewRef.current?.redo();
  const handleDelete = () => previewRef.current?.deleteSelection();
  const handleGroup = () => previewRef.current?.groupSelection();
  const handleReplace = () => previewRef.current?.replaceAsset();
  const handleSelectTool = () => previewRef.current?.selectTool();

  // Called when the API save completes in GsapSlidePreviewEnhanced
  // This triggers the return to composition mode with proper invalidation
  const handleSaveComplete = useCallback(() => {
    console.log('[VideoWorkspacePanels] Save completed, now returning to composition mode');

    // Clear the waiting flag
    waitingForSaveComplete.current = false;

    // Mark slide for invalidation (save just completed, new data is in GCS)
    if (currentSlideId) {
      console.log('[VideoWorkspacePanels] Marking slide for invalidation:', currentSlideId);
      pendingSlideInvalidation.current = currentSlideId;
    }

    // Apply any pending slide updates that were deferred from handleSaveEdit
    // (SSE updates that arrived while user was editing are queued and applied now)
    const currentPendingUpdate = pendingSlideUpdate;
    if (currentPendingUpdate) {
      console.log('[VideoWorkspacePanels] Applying deferred pending slide update:', currentPendingUpdate);
      invalidateSlideCache(currentPendingUpdate.conversationId, currentPendingUpdate.slideId);
      clearPendingSlideUpdate();
    }

    // Seek to end to show completed slide state
    setPreviewSeekToEnd(true);

    // Suppress onSlideStart during composition remount to prevent resetting to first slide
    suppressSlideStartRef.current = true;

    // Return to composition mode
    setCompositionMode(true);

    // Clear the other flags
    pendingReturnToComposition.current = null;
    enteredEditFromComposition.current = false;
  }, [currentSlideId, pendingSlideUpdate, invalidateSlideCache, clearPendingSlideUpdate, setPreviewSeekToEnd]);

  const { selectedCount = 0, canUndo = false, canRedo = false } = editToolbarState || {};

  const handleFullscreen = () => {
    const stage = stageRef.current;
    if (stage) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        stage.requestFullscreen();
      }
    }
  };

  // Handle version switching
  const handleVersionSwitch = useCallback(async (versionNumber: number) => {
    if (!currentSlideId) return;

    // For video mode, use child conversation ID and slide-001
    // For slide mode, use the parent conversation and actual slide ID
    const targetConversationId = effectiveConversationId;
    const targetSlideId = mode === 'video' && currentSlide?.childConversationId ? 'slide-001' : currentSlideId;

    if (!targetConversationId) return;

    // IMPORTANT: Capture playback state BEFORE switching version
    // This prevents the video from playing through all earlier slides after version switch
    const wasPlaying = compositionState.isPlaying;
    const capturedTime = compositionState.currentTime;

    console.log('[VideoWorkspacePanels] Version switch - capturing playback state:', {
      wasPlaying,
      capturedTime,
      currentSlideId,
      isCompositionMode,
    });

    // Pause playback before version switch to prevent race conditions
    if (isCompositionMode && compositionRef.current && wasPlaying) {
      console.log('[VideoWorkspacePanels] Pausing before version switch');
      compositionRef.current.pause();
    }

    setIsSwitchingVersion(true);
    // Suppress slide ID updates during version switch to prevent jumping to slide-001
    suppressSlideStartRef.current = true;
    try {
      console.log('🔄 Switching to version:', versionNumber, 'for slide:', currentSlideId, 'using:', {
        targetConversationId,
        targetSlideId,
        mode,
      });

      // Switch to the new version
      await slidesAPI.switchToVersion(targetConversationId, targetSlideId, versionNumber);

      // Reload the slide data for the CURRENT slide
      const data = await slidesAPI.getGsapSlide(targetSlideId, targetConversationId);

      // ALWAYS override ID to match the original slide ID (for UI consistency)
      data.id = currentSlideId;
      if (!data.path) {
        data.path = `slides/${targetSlideId}/slide.json`;
      }

      // Update slide data first
      setCurrentSlideData(data);

      // Reload versions
      const versionData = await slidesAPI.listVersions(targetConversationId, targetSlideId);
      setVersions(versionData.versions || []);
      setCurrentVersion(versionData.currentVersion);

      // Set to seek to end after refresh to show completed slide state (t=100%)
      setPreviewSeekToEnd(true);
      // Force iframe refresh AFTER data is updated (for single-slide preview)
      triggerPreviewRefresh();

      // Handle video composition mode: invalidate slide and restore position
      if (isCompositionMode && compositionRef.current && currentSlideId) {
        console.log('[VideoWorkspacePanels] Version switched, invalidating slide in composition:', currentSlideId);

        // Invalidate the slide - this clears cache and reloads the current slide
        // Note: invalidateSlide already handles position preservation internally
        compositionRef.current.invalidateSlide(currentSlideId);

        // Give the composition time to reload the slide before seeking/resuming
        // This ensures the new version is fully loaded
        await new Promise(resolve => setTimeout(resolve, 300));

        // Seek to the captured position to restore exactly where we were
        console.log('[VideoWorkspacePanels] Restoring position after version switch:', capturedTime);
        compositionRef.current.seek(capturedTime);

        // Resume playback if it was playing before
        if (wasPlaying) {
          console.log('[VideoWorkspacePanels] Resuming playback after version switch');
          // Small delay to let seek complete before playing
          await new Promise(resolve => setTimeout(resolve, 100));
          compositionRef.current.play();
        }
      }

      // NOTE: We intentionally DON'T call _refreshManifest() in composition mode
      // because it triggers a full manifest reload which resets playback to slide 0.
      // The slide content is already updated via invalidateSlide().
      // For non-composition mode (single-slide preview), manifest refresh is not needed.

      toast.success(`Switched to version ${versionNumber}`);
    } catch (error) {
      console.error('Failed to switch version:', error);
      toast.error('Failed to switch version');
    } finally {
      setIsSwitchingVersion(false);
      // Clear suppression after a short delay to let all state updates settle
      setTimeout(() => {
        suppressSlideStartRef.current = false;
      }, 100);
    }
  }, [currentSlideId, effectiveConversationId, mode, currentSlide, setCurrentSlideData, setVersions, setCurrentVersion, setPreviewSeekToEnd, triggerPreviewRefresh, isCompositionMode, compositionState.isPlaying, compositionState.currentTime]);

  // Handle transition update for a slide
  const handleTransitionUpdate = useCallback(async (slideId: string, transition: TransitionConfig) => {
    if (!conversationId || !videoManifest) return;

    setSavingTransitions(prev => ({ ...prev, [slideId]: true }));
    try {
      console.log('[VideoWorkspacePanels] Updating transition for slide:', slideId, transition);
      await videoManifestAPI.updateSlideTransition(conversationId, slideId, transition);

      // Update local manifest state
      setVideoManifest(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          slides: prev.slides.map(s =>
            s.id === slideId ? { ...s, transitionOut: transition } : s
          ),
        };
      });

      toast.success('Transition updated');
    } catch (error) {
      console.error('Failed to update transition:', error);
      toast.error('Failed to update transition');
    } finally {
      setSavingTransitions(prev => ({ ...prev, [slideId]: false }));
    }
  }, [conversationId, videoManifest]);

  // Handle clearing a slide's custom transition (revert to default)
  const handleTransitionClear = useCallback(async (slideId: string) => {
    if (!conversationId || !videoManifest) return;

    setSavingTransitions(prev => ({ ...prev, [slideId]: true }));
    try {
      console.log('[VideoWorkspacePanels] Clearing transition for slide:', slideId);
      await videoManifestAPI.clearSlideTransition(conversationId, slideId);

      // Update local manifest state
      setVideoManifest(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          slides: prev.slides.map(s =>
            s.id === slideId ? { ...s, transitionOut: undefined } : s
          ),
        };
      });

      toast.success('Transition reset to default');
    } catch (error) {
      console.error('Failed to clear transition:', error);
      toast.error('Failed to reset transition');
    } finally {
      setSavingTransitions(prev => ({ ...prev, [slideId]: false }));
    }
  }, [conversationId, videoManifest]);

  // Get slide transition from manifest
  const getSlideTransition = useCallback((slideId: string): TransitionConfig | null => {
    if (!videoManifest) return null;
    const manifestSlide = videoManifest.slides.find(s => s.id === slideId);
    return manifestSlide?.transitionOut || null;
  }, [videoManifest]);

  // Handle background music change from BackgroundMusicSection
  // Memoized to prevent useEffect in BackgroundMusicSection from firing repeatedly
  const handleBackgroundMusicChange = useCallback((config: {
    url: string;
    volume: number;
    loop: boolean;
  } | null) => {
    console.log('[VideoWorkspacePanels] Background music changed:', config);
    // Sync background music to iframe via VideoCompositionPlayer
    if (compositionRef.current) {
      if (config) {
        compositionRef.current.setBackgroundMusic({
          url: config.url,
          volume: config.volume,
          loop: config.loop,
        });
      } else {
        compositionRef.current.removeBackgroundMusic();
      }
    }
  }, []); // Empty deps - compositionRef is a ref, not state

  // Handle narrator volume change from BackgroundMusicSection
  const handleNarratorVolumeChange = useCallback((volume: number) => {
    console.log('[VideoWorkspacePanels] Narrator volume changed:', volume);
    // Sync narrator volume to iframe via VideoCompositionPlayer
    if (compositionRef.current) {
      compositionRef.current.setNarratorVolume(volume);
    }
  }, []); // Empty deps - compositionRef is a ref, not state

  // Default transition from manifest
  const defaultTransition: TransitionConfig = videoManifest?.defaultTransition || {
    type: 'crossfade',
    duration: 0.5,
    easing: 'power2.inOut',
  };

  // Use composition state when in composition mode, otherwise use single-slide player state
  const effectivePlayerState = isCompositionMode ? {
    isPlaying: compositionState.isPlaying,
    currentTime: compositionState.currentTime,
    duration: compositionState.totalDuration,
  } : playerState;

  const fullscreen = useStudioFullscreen(stageRef, {
    enabled: isB && activeTab === 'studio' && !centred,
    playing: effectivePlayerState.isPlaying,
  });

  const progress = effectivePlayerState.duration > 0
    ? (effectivePlayerState.currentTime / effectivePlayerState.duration) * 100
    : 0;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          // A scene being moved by keyboard drops on Space; that press is not play/pause.
          if (filmstripDragRef.current) break;
          handlePlayPause();
          break;
        case 'ArrowLeft':
          if (e.metaKey || e.ctrlKey) {
            handlePrevSlide();
          }
          break;
        case 'ArrowRight':
          if (e.metaKey || e.ctrlKey) {
            handleNextSlide();
          }
          break;
        case 'Escape':
          if (isEditMode) {
            handleCancelEdit();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevSlide, handleNextSlide, isEditMode]);

  // Listen for confirmed exit events from GsapSlidePreviewEnhanced
  // This handles the case where user confirms exit with unsaved changes
  useEffect(() => {
    const handleEditExitConfirmed = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { action } = customEvent.detail || {};

      if (isVideoMode && !isCompositionMode) {
        console.log('[VideoWorkspacePanels] Edit exit confirmed, setting pending return flag', { action });
        pendingReturnToComposition.current = action || 'cancel';
      }
    };

    window.addEventListener('editExitConfirmed', handleEditExitConfirmed);
    return () => window.removeEventListener('editExitConfirmed', handleEditExitConfirmed);
  }, [isVideoMode, isCompositionMode]);

  // The view bar over the canvas: the view switch, where the video is (or the scene being edited), and the view's controls.
  const viewSwitch = visibleTabs.length > 0 ? (
    <WorkspaceTabStrip
      tabs={visibleTabs}
      active={activeTab}
      isLocked={(tab) => tab === 'blog' && !(slides.length > 0 && slides.every(s => s.status === 'completed'))}
      onSelect={(tab) => { if (centred) dock(); setActiveTab(tab); }}
      variant={isB ? (phone ? 'b-phone' : 'b') : undefined}
    />
  ) : null;
  const studioWithScenes = activeTab === 'studio' && slides.length > 0;
  const viewProgress = studioWithScenes ? (
    <div className="flex min-w-0 items-center gap-2">
      <div className="min-w-0 max-w-[260px]">
        <FlipText text={currentSlide?.title || 'Untitled'} className="text-sm font-medium studio-text-primary truncate" />
      </div>
      {isVideoMode && slides.length > 1 && (
        <div className="flex-shrink-0">
          <FlipText text={`${currentIndex + 1} of ${slides.length}`} className="text-[11px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted inline-block tabular-nums" />
        </div>
      )}
    </div>
  ) : !isFootageMode(creationMode) ? (
    <StageBar
      stage={studio.stage}
      waiting={studio.waiting}
      working={studio.working}
      sceneCounts={{ built: studio.scenes.filter((x) => x.status === 'built').length, total: studio.scenes.length }}
    />
  ) : null;
  const viewControls = studioWithScenes ? (
    <>
      {isEditMode && (
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 dark:bg-amber-500/20">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">Editing</span>
        </div>
      )}
      {versions.length > 0 && (
        <VersionSwitcher versions={versions} currentVersion={currentVersion} onSwitch={handleVersionSwitch} isSwitching={isSwitchingVersion} className="h-7 text-xs" />
      )}
    </>
  ) : null;
  const showViewBar = !isB && (!!viewSwitch || !!viewProgress);
  const progressB = isB && !isFootageMode(creationMode) ? (
    <StudioProgressB
      compact={phone}
      stage={studio.stage}
      waiting={studio.waiting}
      working={studio.working}
      sceneCounts={{ built: studio.scenes.filter((x) => x.status === 'built').length, total: studio.scenes.length }}
      eta={studio.stage === 'build' ? buildEta(studio.scenes, buildNow) : null}
      steps={studio.steps}
      scenes={studio.scenes}
      now={buildNow}
      builtIn={studio.builtIn}
      notesSinceBuild={studio.notesSinceBuild}
    />
  ) : null;
  const goHome = () => {
    // Clear active conversation so hero screen shows properly
    setActiveConversation(null);
    navigate({ to: '/', search: { slide: undefined } });
  };
  const scenesRow = {
    slides,
    slideConversations,
    currentSlideId,
    sensors: filmstripSensors,
    onDragStart: handleFilmstripDragStart,
    onDragEnd: handleFilmstripDragEnd,
    activeDragId: activeFilmstripDragId,
    getStableSlideId,
    onSlideSelect: picked(handleSlideSelect),
    onRequestDelete: handleRequestDeleteSlide,
    onOpenSmartAdd: handleOpenSmartAdd,
    getSlideTransition,
    defaultTransition,
    onTransitionUpdate: handleTransitionUpdate,
    onTransitionClear: handleTransitionClear,
    savingTransitions,
    hasManifest: !!videoManifest,
    deletedSlides: outline?.slides.filter(s => s.isDeleted) ?? [],
    onRestoreDeleted: handleRestoreDeletedSlide,
  };

  return (
    <StudioStateProvider value={studio}>
    <div data-studio-frame={isB && !phone ? '' : undefined} className={phone ? 'h-[100dvh] w-full flex flex-col studio-elegant-bg pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]' : isB ? 'h-full w-full flex flex-col studio-elegant-bg pb-[env(safe-area-inset-bottom)]' : 'h-full w-full flex flex-col studio-elegant-bg'}>
      {isB ? (
        <StudioTopBarB
          conversationId={conversationId}
          phone={phone}
          onOpenMore={() => setMoreOpen(true)}
          title={outline?.title}
          hasOutline={!!outline}
          onHome={goHome}
          showIvyButton={!chatInCenter && !chatPanelOpen}
          onOpenIvy={openIvyB}
          showPublish={isVideoMode && slides.length > 0}
          slides={slides}
          defaultShareTab={activeTab === 'blog' ? 'blog' : 'video'}
          audioAvailable={!!outline && activeTab !== 'narration' && (activeTab !== 'outline' || SHOW_NARRATION_ADVANCED)}
          audioOpen={showAudioPanel}
          audioIsScripts={activeTab === 'outline'}
          onToggleAudio={() => openRightPanelB('audio')}
          reviewsOpen={showReviewsPanel}
          onToggleReviews={() => openRightPanelB('reviews')}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          view={centred && !hasPlan ? undefined : landingPending ? <WorkspaceTabStripSkeleton variant={phone ? 'b-phone' : 'b'} /> : viewSwitch}
          titleExtra={centred ? undefined : viewControls}
          look={outline ?? undefined}
          slotRef={setBarSlot}
          progress={progressB}
        />
      ) : (
        <StudioTopBar
          conversationId={conversationId}
          outlineTitle={outline?.title}
          hasOutline={!!outline}
          onHome={goHome}
          showChatButton={!chatInCenter && !chatPanelOpen}
          onOpenChat={() => setShowChatPanel(true)}
          showPublish={isVideoMode && slides.length > 0}
          slides={slides}
          defaultShareTab={activeTab === 'blog' ? 'blog' : 'video'}
          showReviewsButton={!!outline && !showReviewsPanel}
          onOpenReviews={() => setShowReviewsPanel(true)}
          showAudioButton={!!outline && !showAudioPanel && activeTab !== 'narration' && (activeTab !== 'outline' || SHOW_NARRATION_ADVANCED)}
          audioIsScripts={activeTab === 'outline'}
          onOpenAudio={() => setShowAudioPanel(true)}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MAIN LAYOUT: Left Panel | Center (Canvas + Bottom Bar) | Right Panel
          Animated transitions between Narration and Studio views
          ═══════════════════════════════════════════════════════════════════ */}
      <div className={isB ? 'flex-1 flex min-h-0 overflow-hidden relative' : 'flex-1 flex min-h-0 overflow-hidden'}>

        {/* LEFT PANEL - Chat or Docked Agent (Animated) - Available in all tabs including narration */}
        <StudioIvyColumn
          open={chatPanelOpen}
          expanded={chatPanelExpanded}
          onToggleExpanded={() => setChatPanelExpanded(!chatPanelExpanded)}
          onClose={() => { setShowChatPanel(false); setChatPanelExpanded(false); }}
          conversationId={conversationId}
          slides={slides}
          conversationTitle={isB && !outline ? 'New Video Project' : outline?.title || 'Video Project'}
          systemContext={isB && !outline ? storyDevelopmentContext(conversationId) : ivyChatSystemContext}
          estimateSku={outline?.status === 'draft' ? (outline.slides.some(s => s.interaction?.type) ? 'interactive' : 'standard') : undefined}
          b={isB ? { centred, phone, autoSendMessage: initialMessage, takeover: startedCentred ? { sent: initialMessage, frontDoor } : undefined } : undefined}
        />

        {/* CENTER SECTION - the view bar, then Canvas + Bottom Bar (stageRef, the fullscreen target) */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0" {...(isB ? { ref: stageInRef, 'data-stage': '', inert: centred, style: phone && !centred ? { paddingBottom: SHEET_PEEK } : undefined } : {})}>
        {showViewBar && activeTab !== 'storyboard' && <WorkspaceViewBar left={viewSwitch} center={viewProgress} right={viewControls} />}
        <div
          ref={stageRef}
          className={cn('flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden', fullscreen.on && 'relative', fullscreen.filling && 'fixed inset-0 z-50')}
        >
          <AnimatePresence mode="wait" initial={false}>
            {/* OUTLINE TAB - Show OutlineWorkspacePanel OR Story Development Chat */}
            {activeTab === 'outline' ? (
              outline && !centred && !landingPending ? (
                // Outline EXISTS → show outline panel
                <motion.div
                  key="outline-view"
                  className="flex-1 min-h-0"
                  {...(isB ? bView : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 } })}
                >
                  <OutlineWorkspacePanel
                    conversationId={conversationId}
                  />
                </motion.div>
              ) : (
                // No outline yet → Ivy chat experience on workspace canvas
                <motion.div
                  key="story-view"
                  className={isB ? 'flex-1 min-h-0 flex flex-col bg-b-ground' : 'flex-1 min-h-0 flex flex-col studio-canvas'}
                  {...(isB ? bView : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 } })}
                >
                  {isB ? null : (
                  <div className="flex-1 min-h-0 flex items-center justify-center p-6 md:p-8">
                    <div className="w-full max-w-3xl h-full flex flex-col
                      bg-white dark:bg-zinc-900
                      rounded-2xl overflow-hidden
                      shadow-2xl shadow-black/10 dark:shadow-black/40
                      ring-1 ring-black/5 dark:ring-white/10">
                      <div className="flex-1 min-h-0 flex flex-col">
                        <EmbeddedIvyChat
                          conversationId={conversationId}
                          conversationTitle="New Video Project"
                          systemContext={storyDevelopmentContext(conversationId)}
                          autoSendMessage={initialMessage}
                        />
                      </div>
                    </div>
                  </div>
                  )}
                </motion.div>
              )
            ) : activeTab === 'narration' && outline ? (
              /* NARRATION TAB - Full-width view for script management */
              <motion.div
                key="narration-view"
                className="flex-1 min-h-0"
                {...(isB ? bView : { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 20 }, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as const } })}
              >
                <NarrationView
                  conversationId={conversationId}
                  outline={outline}
                  onScriptsGenerated={() => {
                    // Could trigger a refresh of audio status in studio mode
                    console.log('[VideoWorkspacePanels] Scripts generated/updated');
                  }}
                />
              </motion.div>
            ) : activeTab === 'scenes' ? (
              /* SCENES TAB - the video while it is being made */
              <motion.div
                key="scenes-view"
                className="flex-1 min-h-0 flex flex-col"
                {...(isB ? bView : { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 20 }, transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const } })}
              >
                <BuildView scenes={buildScenes} now={buildNow} focusId={focusSceneId} onFocus={setFocusSceneId} />
              </motion.div>
            ) : activeTab === 'storyboard' ? (
              <motion.div
                key="storyboard-view"
                className="flex-1 min-h-0 flex flex-col"
                {...(isB ? bView : { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 20 }, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as const } })}
              >
                <StudioBarSlot.Provider value={centred ? null : phone ? moreSlot : barSlot}>
                  <StoryboardPanel conversationId={conversationId} onPendingChange={setStoryboardPending} viewBar={isB ? undefined : { left: viewSwitch, center: viewProgress }} openVersion={storyboardAsk} onPageReady={isB ? dock : undefined} />
                </StudioBarSlot.Provider>
              </motion.div>
            ) : activeTab === 'lab' ? (
              /* LAB TAB - Agent-authored HTML prototype documents */
              <motion.div
                key="lab-view"
                className="flex-1 min-h-0 flex flex-col"
                {...(isB ? bView : { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 20 }, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as const } })}
              >
                <LabPanel conversationId={conversationId} />
              </motion.div>
            ) : activeTab === 'blog' ? (
              /* BLOG TAB - Top-level view, iframe preview + floating toolbar */
              <motion.div
                key="blog-view"
                className="flex-1 min-h-0 flex flex-col"
                {...(isB ? bView : { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 20 }, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] as const } })}
              >
                <BlogStudioView conversationId={conversationId} />
              </motion.div>
            ) : (
              <motion.div
                key="studio-view"
                className="flex-1 min-h-0 flex flex-col"
                {...(isB ? bView : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 } })}
              >
              {/* CANVAS AREA - Dot grid with slide */}
              <StudioCanvas
                plannedSlide={plannedSlide}
                plannedIndex={activeOutlineSlides.findIndex(s => s.id === currentSlideId)}
                plannedTotal={activeOutlineSlides.length}
                layouts={templateLayouts}
                generationStatus={currentSlideStatus}
                generatingThinking={currentSlideThinking}
                showComposition={isVideoMode && isCompositionMode && slides.length > 0}
                compositionRef={compositionRef}
                manifest={videoManifest}
                conversationId={conversationId}
                currentSlideId={currentSlideId}
                onCompositionStateChange={handleCompositionStateChange}
                onManifestUpdate={(updatedManifest) => {
                  console.log('[VideoWorkspacePanels] Manifest updated with corrected durations:', {
                    slideCount: updatedManifest.slides.length,
                    totalDuration: updatedManifest.metadata.totalDuration
                  });
                  setVideoManifest(updatedManifest);
                }}
                onSlideStart={(slideId) => {
                  // Skip during return from edit to prevent resetting to first slide
                  if (suppressSlideStartRef.current) {
                    console.log('[VideoWorkspacePanels] Slide started (suppressed):', slideId);
                    return;
                  }
                  console.log('[VideoWorkspacePanels] Slide started:', slideId);
                  followCanvasScene(slideId);
                }}
                onVideoEnd={() => {
                  console.log('[VideoWorkspacePanels] Video ended');
                }}
                onCompositionError={(message) => {
                  console.error('[VideoWorkspacePanels] Composition error:', message);
                  toast.error(message);
                }}
                slideData={currentSlideData}
                previewKey={`${currentSlideId}-r${previewRefreshKey}`}
                previewRef={previewRef}
                previewConversationId={effectiveConversationId}
                onPlayerStateChange={setPlayerState}
                onEditToolbarStateChange={setEditToolbarState}
                onEditModeChange={setEditMode}
                onSaveComplete={handleSaveComplete}
                resolution={projectResolution}
                variant={isB ? 'b' : undefined}
                fullscreen={fullscreen.on}
                buildFrame={buildFrame}
                poster={isB ? stagePoster(compositionState, videoManifest, (id) => slides.find((x) => x.id === id)?.thumbnail) : null}
                onTransitionStarted={isB ? handleTransitionStarted : undefined}
                onManifestLoaded={isB ? handleManifestLoaded : undefined}
                onSlideReloaded={isB ? handleSlideReloaded : undefined}
              />

          {/* EDIT TOOLBAR BAR - Shows above player controls when in edit mode */}
          {isEditMode && (
            <StudioEditToolbar
              canUndo={canUndo}
              canRedo={canRedo}
              selectedCount={selectedCount}
              onSelectTool={handleSelectTool}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onDelete={handleDelete}
              onGroup={handleGroup}
              onReplace={handleReplace}
              onCancel={handleCancelEdit}
              onSave={handleSaveEdit}
            />
          )}

          {/* BOTTOM BAR - Player + Filmstrip. Studio B (not a phone): one dock floating under the stage. */}
          <div
            data-studio-dock={isB && !phone && !fullscreen.on ? '' : undefined}
            className={fullscreen.on
              ? cn('absolute inset-x-0 bottom-0 z-10 transition-opacity duration-300 motion-reduce:transition-none', fullscreen.controlsHidden && 'opacity-0 pointer-events-none')
              : isB ? 'flex-shrink-0 bg-b-chrome' : 'flex-shrink-0 studio-bottombar'}
          >
            {/* Player Controls Row */}
            <StudioPlayerControls
              isVideoMode={isVideoMode}
              slideCount={slides.length}
              currentIndex={currentIndex}
              isPlaying={effectivePlayerState.isPlaying}
              currentTime={effectivePlayerState.currentTime}
              duration={effectivePlayerState.duration}
              progress={progress}
              segmented={isVideoMode && isCompositionMode && videoManifest ? { manifest: videoManifest, playbackState: compositionState } : null}
              onTimelineSeek={(time) => compositionRef.current?.seek(time)}
              onTimelineSlideClick={(slideId) => compositionRef.current?.goToSlide(slideId)}
              onSeek={(seekTime) => {
                if (isCompositionMode) {
                  compositionRef.current?.seek(seekTime);
                } else {
                  previewRef.current?.seek(seekTime);
                }
              }}
              onRestart={picked(handleRestart)}
              onPrev={picked(handlePrevSlide)}
              onNext={picked(handleNextSlide)}
              onPlayPause={handlePlayPause}
              isEditMode={isEditMode}
              onEnterEdit={handleEnterEditMode}
              onFullscreen={isB ? fullscreen.toggle : handleFullscreen}
              showFilmstrip={showFilmstrip && !fullscreen.on}
              onToggleFilmstrip={() => setShowFilmstrip(!showFilmstrip)}
              fullscreen={isB ? fullscreen.on : undefined}
              phone={phone}
              scene={isB && isVideoMode && currentSlide ? { title: currentSlide.title || 'Untitled', number: currentIndex + 1, total: slides.length } : undefined}
              playable={isB ? !stageUnbuilt : undefined}
              variant={isB ? 'b' : undefined}
            />

            {/* Filmstrip Row (Animated) - Only in video mode; Direction B draws the scenes as a timeline */}
            {isVideoMode && (isB ? (
              <AnimatePresence initial={false}>
              {showFilmstrip && !fullscreen.on && (
                <motion.div
                  key="timeline"
                  data-timeline-slot=""
                  className="overflow-hidden"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={bMove()}
                >
                <StudioTimelineB
                  {...scenesRow}
                  manifest={videoManifest}
                  time={effectivePlayerState.currentTime}
                  wholeVideo={isCompositionMode}
                  sceneDuration={effectivePlayerState.duration}
                  drafts={Object.fromEntries(buildScenes.flatMap((x) => (x.status !== 'built' && x.thumbnail ? [[x.id, x.thumbnail]] : [])))}
                  scenes={studio.scenes}
                  now={buildNow}
                  onSeek={picked((time: number, slideId: string) => {
                    compositionRef.current?.seek(time);
                    setCurrentSlideId(slideId);
                  })}
                />
                </motion.div>
              )}
              </AnimatePresence>
            ) : (
              <StudioFilmstrip conversationId={conversationId} {...scenesRow} showFilmstrip={showFilmstrip} />
            ))}
          </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        </div>

        {/* RIGHT PANEL - Audio (Animated) - Hidden in narration mode */}
        <StudioAudioPanel
          sheet={phone}
          visible={showAudioPanel && activeTab !== 'narration' && (activeTab !== 'outline' || SHOW_NARRATION_ADVANCED)}
          open={showAudioPanel}
          isOutlineTab={activeTab === 'outline'}
          scriptsOutline={activeTab === 'outline' ? outline : null}
          conversationId={conversationId}
          sceneConversationId={effectiveConversationId}
          currentSlideId={currentSlideId}
          currentSlideData={currentSlideData}
          mode={mode}
          onClose={() => setShowAudioPanel(false)}
          onMusicChange={handleBackgroundMusicChange}
          onNarratorVolumeChange={handleNarratorVolumeChange}
        />

        {/* RIGHT PANEL — Reviews (Animated). Mounts only after a project has an outline. */}
        <StudioReviewsPanel
          sheet={phone}
          open={showReviewsPanel}
          conversationId={conversationId}
          onClose={() => setShowReviewsPanel(false)}
          onSeekTo={(timestamp, slideId) => {
            // Seek the composition player + select that slide so the user is at the moment in question.
            compositionRef.current?.seek(timestamp, { pauseAfterSeek: true });
            if (slideId) setCurrentSlideId(slideId);
          }}
        />

      </div>

      {phone && moreOpen && (
        <PhoneMoreSheetB
          conversationId={conversationId}
          hasOutline={!!outline}
          showPublish={isVideoMode && slides.length > 0}
          slides={slides}
          defaultShareTab={activeTab === 'blog' ? 'blog' : 'video'}
          audioAvailable={!!outline && activeTab !== 'narration' && (activeTab !== 'outline' || SHOW_NARRATION_ADVANCED)}
          audioIsScripts={activeTab === 'outline'}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          onAudio={() => openRightPanelB('audio')}
          onReviews={() => openRightPanelB('reviews')}
          onClose={() => setMoreOpen(false)}
          pageSlotRef={activeTab === 'storyboard' ? setMoreSlot : undefined}
        />
      )}

      {/* Smart Add Slide Modal */}
      <SmartAddSlideModal
        open={smartAddModalOpen}
        onOpenChange={setSmartAddModalOpen}
        layouts={templateLayouts}
        onSubmit={handleSmartAddSubmit}
        isSubmitting={isSmartAdding}
      />

      {/* Delete Slide Confirmation */}
      <ConfirmationDialog
        open={!!deleteConfirmSlide}
        onOpenChange={(open) => { if (!open) setDeleteConfirmSlide(null); }}
        title="Delete Slide?"
        description={
          <span>
            Are you sure you want to delete <strong>"{deleteConfirmSlide?.title || 'this slide'}"</strong>?
            <br />
            <span className="text-muted-foreground">You can undo this for a few seconds after deletion.</span>
          </span>
        }
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleConfirmDeleteSlide}
      />
    </div>
    </StudioStateProvider>
  );
};
